import { useInfiniteQuery } from '@tanstack/react-query';
import { Dialog, DialogBody, DialogFooter } from '@blueprintjs/core';
import { fetchPreviewRows, type BatchDetail, type PreviewRowKind, type PreviewSample, type SheetRow, type SkippedRow } from './api';
import { leadImportKeys } from './keys';
import { ErrorNote, fmtInt } from './ui';

const LEAD_COLUMNS = ['business_name', 'first_name', 'last_name', 'phone', 'alternate_phone', 'email', 'state', 'note'];
const heading = (field: string) => field.charAt(0).toUpperCase() + field.slice(1).replace(/_/g, ' ');

const TITLE: Record<PreviewRowKind, (n: number) => string> = {
  leads: (n) => `${fmtInt(n)} ${n === 1 ? 'lead' : 'leads'} as they will be saved`,
  merged: (n) => `${fmtInt(n)} ${n === 1 ? 'lead' : 'leads'} merged from repeated rows`,
  skipped: (n) => `${fmtInt(n)} ${n === 1 ? 'row' : 'rows'} skipped, no usable phone`,
};
const LOADING_TITLE: Record<PreviewRowKind, string> = { leads: 'Leads', merged: 'Merged repeats', skipped: 'Skipped rows' };

const NOTE: Record<PreviewRowKind, string> = {
  leads: 'One lead per phone number. Values come from the first row with that phone; other values are kept as alternatives.',
  merged: 'These phones appear on more than one row. The rows become one lead; differing values are kept as alternatives.',
  skipped: 'These rows are not imported. Fix the phone in the file, or choose another phone column, and upload again.',
};

const SKIP_REASON: Record<SkippedRow['reason'], string> = {
  missing_phone: 'No phone',
  invalid_phone: 'Phone not valid',
};

/** The rows behind one count of the preview, a page at a time. */
export function PreviewRowsDialog({ batch, kind, onClose }: { batch: BatchDetail; kind: PreviewRowKind; onClose: () => void }) {
  const generation = batch.preview?.generation ?? 0;
  const q = useInfiniteQuery({
    queryKey: leadImportKeys.previewRows(batch.id, generation, kind),
    queryFn: ({ pageParam }) => fetchPreviewRows(batch.id, kind, pageParam),
    initialPageParam: 0,
    getNextPageParam: (last) => (last.offset + last.rows.length < last.total ? last.offset + last.rows.length : undefined),
  });
  const pages = q.data?.pages ?? [];
  const total = pages[0]?.total;
  const shown = pages.reduce((n, p) => n + p.rows.length, 0);
  const sheetName = new Map(batch.sheets.map((s) => [s.id, s.sheet_name]));

  return (
    <Dialog isOpen onClose={onClose} title={total == null ? LOADING_TITLE[kind] : TITLE[kind](total)} className="li-filedialog">
      <DialogBody>
        <p className="li-muted li-rowsdialog__note">{NOTE[kind]}</p>
        <ErrorNote error={q.error} />
        {q.isPending && <p className="li-muted">Loading…</p>}
        {q.data && (
          <div className="li-rowsdialog__table">
            {kind === 'skipped'
              ? <SkippedTable rows={pages.flatMap((p) => p.rows as SkippedRow[])} sheetName={sheetName} />
              : <SampleTable samples={pages.flatMap((p) => p.rows as PreviewSample[])} sheets={batch.sheets} showOrigins={kind === 'merged'} />}
          </div>
        )}
      </DialogBody>
      <DialogFooter
        actions={<button type="button" className="li-btn li-btn--quiet" onClick={onClose}>Close</button>}
      >
        {total != null && total > 0 && (
          <span className="li-rowsdialog__count">
            Showing {fmtInt(shown)} of {fmtInt(total)}
            {q.hasNextPage && (
              <button type="button" className="li-btn li-btn--link" disabled={q.isFetchingNextPage} onClick={() => q.fetchNextPage()}>
                {q.isFetchingNextPage ? 'Loading…' : 'Show more'}
              </button>
            )}
          </span>
        )}
      </DialogFooter>
    </Dialog>
  );
}

/** Leads as they will be saved; with showOrigins, every row each one came from. */
export function SampleTable({ samples, sheets, showOrigins = false }: {
  samples: (PreviewSample & { origins?: [number, number][] })[]; sheets: SheetRow[]; showOrigins?: boolean;
}) {
  const sheetName = new Map(sheets.map((s) => [s.id, s.sheet_name]));
  const where = (sheet: number, row: number) => `${sheets.length > 1 ? `${sheetName.get(sheet)} ` : ''}row ${row}`;
  return (
    <div className="li-tablewrap">
      <table className="li-table li-table--compact">
        <thead>
          <tr>
            <th>{showOrigins ? 'Rows' : 'From'}</th>
            {LEAD_COLUMNS.map((c) => <th key={c}>{heading(c)}</th>)}
            {!showOrigins && <th className="r">Merged</th>}
            <th>Extra</th>
          </tr>
        </thead>
        <tbody>
          {samples.map((x) => {
            const alts = Object.entries(x.extra_data.alternatives ?? {}).map(([k, v]) => `${k}: ${v.map((a) => a.v).join(' / ')}`);
            const extra = Object.keys(x.extra_data.unmapped ?? {});
            return (
              <tr key={x.phone_normalized}>
                <td className="li-muted li-nowrap">
                  {showOrigins && x.origins
                    ? x.origins.map(([s, r]) => <div key={`${s}:${r}`}>{where(s, r)}</div>)
                    : where(x.first_sheet, x.first_row)}
                </td>
                {LEAD_COLUMNS.map((c) => <td key={c} className="li-ellipsis">{x.fields[c] ?? ''}</td>)}
                {!showOrigins && <td className="r">{x.row_count > 1 ? x.row_count : ''}</td>}
                <td className="li-muted li-nowrap" title={[...alts, ...extra].join('\n')}>{alts.length + extra.length ? `${alts.length + extra.length} values` : ''}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function SkippedTable({ rows, sheetName }: { rows: SkippedRow[]; sheetName: Map<number, string> }) {
  const multiSheet = new Set(rows.map((r) => r.sheet)).size > 1;
  return (
    <div className="li-tablewrap">
      <table className="li-table li-table--compact">
        <thead><tr><th>Row</th><th>Why</th><th>Phone cells</th><th>Business name</th><th>First name</th><th>Last name</th><th>Email</th></tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={`${r.sheet}:${r.row}`}>
              <td className="li-muted li-nowrap">{multiSheet ? `${sheetName.get(r.sheet)} ` : ''}row {r.row}</td>
              <td className="li-nowrap">{SKIP_REASON[r.reason]}</td>
              <td>
                {r.phones.map((p, i) => (
                  <div key={i} className="li-nowrap">
                    <span className="li-muted">{p.header || `Column ${i + 1}`}: </span>
                    {p.raw?.trim() ? p.raw : <span className="li-muted">empty</span>}
                  </div>
                ))}
              </td>
              <td className="li-ellipsis">{r.fields.business_name ?? ''}</td>
              <td className="li-ellipsis">{r.fields.first_name ?? ''}</td>
              <td className="li-ellipsis">{r.fields.last_name ?? ''}</td>
              <td className="li-ellipsis">{r.fields.email ?? ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
